import { EncounterService } from '@quromedical/fhir-common';
import { first } from '@quromedical/utils';
import '@quromedical/utils';
import { isValid, parseISO, differenceInCalendarDays } from 'date-fns';
import { compose, resolveConstant } from '../composer';

const isOlderThanOneDay = startDateString => {
  if (!startDateString) return false;
  const startDate = parseISO(startDateString);
  if (!isValid(startDate)) return false;
  const currentDate = new Date();
  const timeDifference = differenceInCalendarDays(currentDate, startDate);
  return timeDifference >= 1;
};

const assertCanCreateNewAdmission = _ref => {
  let {
    activeAdmissions,
    newAdmission
  } = _ref;

  if (activeAdmissions.length === 0) {
    return undefined;
  }

  const hasAdmissionInProgress = activeAdmissions.some(a => a.status === 'in-progress');

  if (hasAdmissionInProgress) {
    return {
      type: 'error',
      messages: [`Cannot create a new admission because there is an active admissions`]
    };
  }

  const isCurrentAdmissionsInPaused = activeAdmissions.filter(a => a.status === 'onleave');
  const isAdmissionsTypeOfServiceValid = isCurrentAdmissionsInPaused.every(a => a.service !== (newAdmission === null || newAdmission === void 0 ? void 0 : newAdmission.service));

  if (isAdmissionsTypeOfServiceValid) {
    return undefined;
  }

  const findServiceType = Object.values(EncounterService).indexOf(newAdmission === null || newAdmission === void 0 ? void 0 : newAdmission.service);
  const serviceType = Object.keys(EncounterService)[findServiceType];
  return {
    type: 'error',
    messages: [`Admission cannot have the same service type of ${serviceType} as the previous paused admissions`]
  };
};

const assertCanPauseAnAdmission = _ref2 => {
  let {
    activeAdmissions
  } = _ref2;
  const isCurrentAdmissionsInProgress = activeAdmissions.filter(a => a.status === 'in-progress' || a.status === 'onleave');

  if (isCurrentAdmissionsInProgress.length > 1) {
    return {
      type: 'error',
      messages: [`Only one admission can be paused at a time`]
    };
  }

  const isCurrentAdmissionStatusValid = isCurrentAdmissionsInProgress.find(a => a.status === 'onleave');

  if (!isCurrentAdmissionStatusValid) {
    return undefined;
  }

  if (isCurrentAdmissionStatusValid) {
    return {
      type: 'error',
      messages: [`Admission cannot be paused because it is in the wrong state`]
    };
  }
};

const assertCanResumeAdmission = _ref3 => {
  let {
    activeAdmissions
  } = _ref3;
  const isCurrentAdmissionsInProgress = activeAdmissions.filter(a => a.status === 'in-progress' || a.status === 'onleave');

  if (isCurrentAdmissionsInProgress.length > 2) {
    return {
      type: 'error',
      messages: [`Only one admission can be resume at a time`]
    };
  }

  const isCurrentAdmissionStatusValid = isCurrentAdmissionsInProgress.find(a => a.status === 'in-progress');

  if (!isCurrentAdmissionStatusValid) {
    return undefined;
  }

  if (isCurrentAdmissionStatusValid) {
    return {
      type: 'error',
      messages: ['Patient already has an active admission that is in progress']
    };
  }
};

const assertCanDischargeAdmission = _ref4 => {
  let {
    activeAdmissions
  } = _ref4;
  const isCurrentAdmissionStatusValid = activeAdmissions.filter(a => a.status === 'in-progress');

  if (isCurrentAdmissionStatusValid.length <= 0) {
    return {
      type: 'error',
      messages: [`Admission cannot be discharged because it is not in-progress`]
    };
  }

  if (isCurrentAdmissionStatusValid.length > 1) {
    return {
      type: 'error',
      messages: [`Cannot discharge more than one admission at a time`]
    };
  }

  const hasStatusHistory = isCurrentAdmissionStatusValid.map(history => history.statusHistory).flat();

  if (hasStatusHistory.length > 0) {
    const isValidStatusHistory = hasStatusHistory.find(a => (a === null || a === void 0 ? void 0 : a.status) === 'in-progress' && a.period.end === undefined);

    if ((isValidStatusHistory === null || isValidStatusHistory === void 0 ? void 0 : isValidStatusHistory.status) !== 'in-progress' && (isValidStatusHistory === null || isValidStatusHistory === void 0 ? void 0 : isValidStatusHistory.period.end) === undefined) {
      return {
        type: 'error',
        messages: [`Admission cannot be discharged because the statusHistory is in the wrong state`]
      };
    }

    if (isCurrentAdmissionStatusValid) {
      return undefined;
    }
  }
};

const assertCanInvalidateAdmission = _ref5 => {
  var _admission$period;

  let {
    activeAdmissions
  } = _ref5;
  const isCurrentAdmissionStatusValid = activeAdmissions.filter(a => a.status === 'in-progress');

  if (isCurrentAdmissionStatusValid.length === 0) {
    return {
      type: 'error',
      messages: [`Admission cannot be invalidated because it is not in-progress`]
    };
  }

  if (isCurrentAdmissionStatusValid.length > 1) {
    return {
      type: 'error',
      messages: [`Cannot invalidate more than one admission at a time`]
    };
  }

  const admission = first(isCurrentAdmissionStatusValid);
  const olderThanOneDay = isOlderThanOneDay(admission === null || admission === void 0 ? void 0 : (_admission$period = admission.period) === null || _admission$period === void 0 ? void 0 : _admission$period.start);

  if (olderThanOneDay) {
    return {
      type: 'error',
      messages: ['Admission cannot be invalidated because it was create more than 1 day ago']
    };
  }

  const hasStatusHistory = isCurrentAdmissionStatusValid.map(history => history.statusHistory).flat();

  if (hasStatusHistory.length > 0) {
    const isValidStatusHistory = hasStatusHistory.find(a => (a === null || a === void 0 ? void 0 : a.status) === 'in-progress' && a.period.end === undefined);

    if ((isValidStatusHistory === null || isValidStatusHistory === void 0 ? void 0 : isValidStatusHistory.status) !== 'in-progress' && (isValidStatusHistory === null || isValidStatusHistory === void 0 ? void 0 : isValidStatusHistory.period.end) === undefined) {
      return {
        type: 'error',
        messages: ['Admission cannot be invalidated because the statusHistory is in the wrong state']
      };
    }

    if (isCurrentAdmissionStatusValid) {
      return undefined;
    }
  }
};

const AdmissionAssertions = {
  'new-admission': [assertCanCreateNewAdmission],
  'pause-admission': [assertCanPauseAnAdmission],
  'resume-admission': [assertCanResumeAdmission],
  'discharge-admission': [assertCanDischargeAdmission],
  'invalidate-admission': [assertCanInvalidateAdmission]
};
/**
 * a function that takes in a set of parameters and based on that determines if a new admission can be created
 */

export const validateAdmissionRequest = compose({
  getRuleName: _ref6 => {
    let {
      admissionType
    } = _ref6;
    return admissionType;
  },
  assertions: AdmissionAssertions,
  defaultAssertions: [],
  defaultResolver: resolveConstant(true)
});
//# sourceMappingURL=admissions.js.map